import React from 'react';
import { motion } from 'framer-motion';
import { Send, Users, Star, Layers } from 'lucide-react';
import { Helmet } from "react-helmet-async";

const Hero = () => {
    return (
        <>
            {/* ✅ SEO Meta Tags */}
            <Helmet>
                <title>
                    CodeBytz Technologies | Software & Web Development Company in Sri Lanka
                </title>

                <meta
                    name="description"
                    content="CodeBytz Technologies is a Sri Lanka-based software company specializing in web development, mobile apps, UI/UX design, and custom IT solutions."
                />

                <meta
                    name="keywords"
                    content="CodeBytz Technologies, software company Sri Lanka, web development Jaffna, React developers, Laravel development"
                />

                <meta name="robots" content="index, follow" />

                <link rel="canonical" href="https://codebytz.com/" />

                {/* Open Graph */}
                <meta property="og:title" content="CodeBytz Technologies" />
                <meta
                    property="og:description"
                    content="Professional web and mobile app development services in Sri Lanka."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://codebytz.com/" />
            </Helmet>

            <section id="home" className="relative min-h-screen flex items-center justify-center pt-32 pb-20 overflow-hidden bg-light-bg">
                
                {/* Background Digital Animation (Removed to use CSS fallback) */}
                <div className="absolute inset-0 z-0 video-fallback">
                    <div className="absolute inset-0 grid-pattern opacity-[0.05]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/50 to-light-bg z-10" />
                </div>


                <div className="container mx-auto px-6 relative z-10 w-full">
                    <div className="flex flex-col items-center text-center max-w-6xl mx-auto">

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            className="mb-10 px-6 py-2 rounded-full glass-effect border-brand-teal/30 text-brand-light text-xs font-black uppercase tracking-[0.3em] shadow-lg shadow-brand-teal/5"
                        >
                            Architecting Digital Excellence
                        </motion.div>

                        {/* ✅ SEO Optimized H1 */}
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-5xl md:text-8xl font-black mb-10 text-dark-text leading-[1] perspective tracking-tighter"
                        >
                            We builds <span className="text-gradient">reliable software</span> <br />
                            that <span className="preserve-3d inline-block animate-float italic decoration-brand-teal/20 underline underline-offset-[12px]">ships on time</span>
                        </motion.h1>

                        {/* ✅ SEO Optimized Paragraph */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 0.5 }}
                            className="text-slate-600 text-lg md:text-2xl max-w-4xl mb-14 leading-normal font-medium"
                        >
                            CodeBytz Technologies is a leading software company in Sri Lanka specializing in web development, mobile applications, UI/UX design, and scalable digital solutions for modern businesses.
                        </motion.p>

                        {/* ✅ Hidden SEO Boost */}
                        <p className="hidden">
                            CodeBytz Technologies provides custom software development, React web applications,
                            Laravel backend systems, and mobile app solutions in Jaffna and across Sri Lanka.
                        </p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.7 }}
                            className="flex flex-col sm:flex-row gap-5 mb-24"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(0, 128, 128, 0.5)" }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                                className="bg-brand-teal text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-4 shadow-2xl shadow-brand-teal/30 transition-all group"
                            >
                                Start Project
                                <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.05)" }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                                className="glass-effect text-dark-text px-10 py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-4 border border-slate-200 transition-all font-bold"
                            >
                                View Work
                                <Layers size={20} className="text-brand-teal" />
                            </motion.button>
                        </motion.div>

                        {/* Stats Section */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
                            {[
                                { label: 'Project Delivered', value: '250+', icon: Layers },
                                { label: 'Client Ratings', value: '4.9/5', icon: Star },
                                { label: 'Active Users', value: '10K+', icon: Users },
                            ].map((stat, index) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.9 + index * 0.1 }}
                                    className="glass-effect p-8 rounded-[32px] flex flex-col items-center gap-4 hover:translate-y-[-10px] transition-all cursor-default group border border-white/5 relative overflow-hidden"
                                >
                                    <div className="absolute top-0 left-0 w-full h-[2px] bg-brand-teal/20 group-hover:bg-brand-teal transition-colors" />
                                    <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-all flex items-center justify-center shadow-inner">
                                        <stat.icon size={28} />
                                    </div>
                                    <div className="text-3xl font-black text-dark-text">{stat.value}</div>
                                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em]">{stat.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Background Decor */}
                <div className="absolute -bottom-48 -left-48 w-[40vw] h-[40vw] bg-brand-teal/5 blur-[100px] rounded-full pointer-events-none" />
                <div className="absolute -top-48 -right-48 w-[40vw] h-[40vw] bg-brand-teal/5 blur-[100px] rounded-full pointer-events-none" />

                {/* Scroll Indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2 }}
                    className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3"
                >
                </motion.div>
            </section>
        </>
    );
};

export default Hero;